import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Link, useParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import useLocalStorage from '../../utils/useLocalStorage';
import PageHeader from '../../components/ui/PageHeader';
import UiCard from '../../components/ui/UiCard';
import { paginationItems, FORMA_LINK_BTN } from '../../utils/listPageUi';
import TrainerDetail from '../../components/trainers/TrainerDetail';
import TrainerForm from '../../components/trainers/TrainerForm';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  UserGroupIcon,
  BriefcaseIcon,
  ClipboardDocumentCheckIcon,
  ArrowLeftIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

// Les composants TrainerDetail et TrainerForm ont été déplacés vers des fichiers séparés

const TrainersPage = () => {
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [specialityFilter, setSpecialityFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [trainersPerPage] = useState(10);
  
  // Multi-select state for bulk delete
  const [selectedTrainers, setSelectedTrainers] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  
  // Utiliser notre hook personnalisé pour gérer les formateurs avec localStorage
  const {
    items: trainers,
    deleteItem: deleteTrainer,
    refreshData,
  } = useLocalStorage('trainers');
  
  // Rafraîchir les données des formateurs lors du chargement de la page
  useEffect(() => {
    console.log('Refreshing trainers data');
    refreshData();
  }, [location, refreshData]);

  const stats = [
    {
      title: 'Total des formateurs',
      value: trainers.length.toString(),
      hint: 'Dans votre annuaire',
      icon: UserGroupIcon,
      iconWrap:
        'bg-forma-100 text-forma-700 shadow-inner dark:bg-forma-950/50 dark:text-forma-400',
    },
    {
      title: 'Cours assignés',
      value: trainers.reduce((acc, trainer) => acc + (trainer.courses?.length || 0), 0).toString(),
      hint: 'Liens formation–formateur',
      icon: ClipboardDocumentCheckIcon,
      iconWrap:
        'bg-emerald-100 text-emerald-800 shadow-inner dark:bg-emerald-950/45 dark:text-emerald-400',
    },
    {
      title: 'Spécialités',
      value: [...new Set(trainers.map((t) => t.specialty).filter(Boolean))].length.toString(),
      hint: 'Domaines distincts',
      icon: BriefcaseIcon,
      iconWrap: 'bg-amber-100 text-amber-800 shadow-inner dark:bg-amber-950/40 dark:text-amber-400',
    },
  ];

  const specialities = [...new Set(trainers.map((trainer) => trainer.specialty).filter(Boolean))];

  const filterTabs = useMemo(
    () => [
      { id: 'all', name: 'Toutes les spécialités' },
      ...specialities.map((s) => ({ id: s, name: s })),
    ],
    [specialities]
  );

  const tabCounts = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const base = trainers.filter((trainer) => {
      if (!q) return true;
      return (
        trainer.name.toLowerCase().includes(q) ||
        trainer.email.toLowerCase().includes(q) ||
        (trainer.specialty ? trainer.specialty.toLowerCase().includes(q) : false)
      );
    });
    const counts = { all: base.length };
    specialities.forEach((spec) => {
      counts[spec] = base.filter((t) => t.specialty === spec).length;
    });
    return counts;
  }, [trainers, searchTerm, specialities]);

  const allTrainers = trainers;
  const filteredTrainers = allTrainers.filter(trainer => {
    const matchesSearch = trainer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         trainer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (trainer.specialty ? trainer.specialty.toLowerCase().includes(searchTerm.toLowerCase()) : false);
    
    if (!matchesSearch) return false;
    
    if (specialityFilter === 'all') return true;
    return trainer.specialty === specialityFilter;
  });
  
  const paginatedTrainers = filteredTrainers.slice((currentPage - 1) * trainersPerPage, currentPage * trainersPerPage);
  const totalTrainers = filteredTrainers.length;
  const totalPages = Math.ceil(totalTrainers / trainersPerPage);

  const pageSliceLabel =
    totalTrainers === 0
      ? '—'
      : `${Math.min((currentPage - 1) * trainersPerPage + 1, totalTrainers)}–${Math.min(currentPage * trainersPerPage, totalTrainers)}`;

  const pageNav = useMemo(
    () =>
      paginationItems(Math.min(Math.max(currentPage, 1), Math.max(totalPages, 1)), Math.max(totalPages, 1)),
    [currentPage, totalPages]
  );

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleDelete = async (trainerId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce formateur ?')) {
      try {
        // Utilise la fonction du hook pour supprimer le formateur
        const success = deleteTrainer(trainerId);
        if (!success) {
          throw new Error('Échec de la suppression');
        }
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Une erreur est survenue lors de la suppression');
      }
    }
  };

  // Multi-select functions
  const handleSelectTrainer = (trainerId) => {
    setSelectedTrainers(prev => {
      if (prev.includes(trainerId)) {
        // Remove from selection
        return prev.filter(id => id !== trainerId);
      } else {
        // Add to selection
        return [...prev, trainerId];
      }
    });
  };

  const handleSelectAll = () => {
    if (isSelectAll) {
      // Deselect all
      setSelectedTrainers([]);
      setIsSelectAll(false);
    } else {
      // Select all visible trainers
      const visibleTrainerIds = paginatedTrainers.map(t => t.id);
      setSelectedTrainers(visibleTrainerIds);
      setIsSelectAll(true);
    }
  };

  const handleBulkDelete = async () => {
    try {
      // Delete selected trainers using the hook function
      let successCount = 0;
      selectedTrainers.forEach(trainerId => {
        const success = deleteTrainer(trainerId);
        if (success) successCount++;
      });
      
      // Clear selection
      setSelectedTrainers([]);
      setIsSelectAll(false);
      setShowBulkDeleteModal(false);
      
      if (successCount === selectedTrainers.length) {
        // All deletions successful - the hook will refresh the data automatically
      } else {
        throw new Error(`Seulement ${successCount}/${selectedTrainers.length} formateurs ont été supprimés`);
      }
    } catch (error) {
      console.error('Error deleting trainers:', error);
      alert('Erreur lors de la suppression des formateurs');
    }
  };

  // Auto-update select all state based on selected trainers
  useEffect(() => {
    if (selectedTrainers.length === 0) {
      setIsSelectAll(false);
    } else {
      const visibleTrainerIds = paginatedTrainers.map(t => t.id);
      const allVisibleSelected = visibleTrainerIds.every(id => selectedTrainers.includes(id));
      setIsSelectAll(allVisibleSelected && visibleTrainerIds.length > 0);
    }
  }, [selectedTrainers, paginatedTrainers]);

  const formaLinkBtn = FORMA_LINK_BTN;

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
          <div className="space-y-6">
            <PageHeader
              title="Formateurs"
              description="Gestion des formateurs et de leurs cours"
              actions={
                <Link to="/trainers/add" className={formaLinkBtn}>
                  <PlusIcon className="h-5 w-5" />
                  <span>Nouveau formateur</span>
                </Link>
              }
            />

            <UiCard padding={false} className="overflow-hidden">
              <div className="border-b border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-slate-50/80 dark:border-slate-700 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
                <div className="grid gap-6 p-6 sm:grid-cols-3 sm:gap-5">
                  {stats.map((stat, index) => (
                    <motion.div
                      key={stat.title}
                      layout
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ type: 'spring', stiffness: 380, damping: 28, delay: index * 0.06 }}
                      whileHover={{ y: -3 }}
                      className="group relative rounded-2xl border border-slate-200/80 bg-white/80 p-[1px] shadow-sm backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-950/55"
                    >
                      <div className="relative overflow-hidden rounded-[0.9375rem] bg-gradient-to-b from-white/90 to-white/35 p-4 dark:from-slate-950/95 dark:to-slate-950/65">
                        <div
                          aria-hidden
                          className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br from-forma-200/35 to-transparent opacity-70 blur-2xl transition group-hover:opacity-100 dark:from-forma-600/25"
                        />
                        <div className="relative flex gap-4">
                          <div
                            className={clsx(
                              'relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-2 ring-white/70 dark:ring-slate-800/80',
                              stat.iconWrap
                            )}
                          >
                            <stat.icon className="h-6 w-6" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                              {stat.title}
                            </p>
                            <p className="mt-0.5 text-3xl font-bold tabular-nums tracking-tight text-slate-900 dark:text-white">
                              {stat.value}
                            </p>
                            <p className="mt-1 border-t border-slate-100 pt-2 text-xs text-slate-500 dark:border-slate-700/70 dark:text-slate-400">
                              {stat.hint}
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="border-b border-slate-200 bg-white/40 px-5 py-4 dark:border-slate-700 dark:bg-slate-900/35">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Spécialité
                      </span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                        Compte après recherche
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 lg:-ms-1">
                      {filterTabs.map((tab) => (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => {
                            setSpecialityFilter(tab.id);
                            setCurrentPage(1);
                          }}
                          className={clsx(
                            'inline-flex items-center rounded-full px-3.5 py-2 text-sm font-medium transition-all',
                            specialityFilter === tab.id
                              ? 'scale-[1.02] bg-forma-600 text-white shadow-md shadow-forma-600/25'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                          )}
                        >
                          <span className={tab.id !== 'all' ? 'max-w-[10rem] truncate' : ''}>{tab.name}</span>
                          <span
                            className={clsx(
                              'ml-2 min-w-[1.75rem] rounded-full px-2 py-0.5 text-center text-xs font-semibold tabular-nums',
                              specialityFilter === tab.id
                                ? 'bg-white/20 text-white'
                                : 'bg-white/70 text-slate-800 dark:bg-slate-950 dark:text-slate-200'
                            )}
                          >
                            {tabCounts[tab.id] ?? 0}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="relative w-full lg:max-w-sm lg:flex-shrink-0">
                    <label htmlFor="trainer-search" className="sr-only">
                      Rechercher un formateur
                    </label>
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                      id="trainer-search"
                      type="search"
                      autoComplete="off"
                      placeholder="Nom, email ou spécialité…"
                      value={searchTerm}
                      onChange={handleSearch}
                      className={clsx(
                        'w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-forma-500 focus:outline-none focus:ring-2 focus:ring-forma-500/25 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500',
                        searchTerm.trim() ? 'pr-10' : 'pr-4'
                      )}
                    />
                    {searchTerm.trim() !== '' ? (
                      <button
                        type="button"
                        aria-label="Effacer la recherche"
                        onClick={() => {
                          setSearchTerm('');
                          setCurrentPage(1);
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              {(searchTerm.trim() !== '' || specialityFilter !== 'all') && (
                <div className="flex flex-wrap items-center gap-2 border-b border-forma-200/60 bg-forma-50/50 px-5 py-2.5 dark:border-forma-900/35 dark:bg-forma-950/25">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-forma-800 dark:text-forma-400">
                    Filtres actifs
                  </span>
                  {searchTerm.trim() !== '' ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-forma-200/70 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-forma-900/60 dark:bg-slate-950 dark:text-slate-200">
                      « {searchTerm.trim()} »
                    </span>
                  ) : null}
                  {specialityFilter !== 'all' ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-forma-200/70 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-forma-900/60 dark:bg-slate-950 dark:text-slate-200">
                      {specialityFilter}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('');
                      setSpecialityFilter('all');
                      setCurrentPage(1);
                    }}
                    className="ml-auto rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    Réinitialiser
                  </button>
                </div>
              )}

              {selectedTrainers.length > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-forma-200/80 bg-forma-50/90 px-5 py-3 dark:border-forma-900/50 dark:bg-forma-950/25">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      {selectedTrainers.length} formateur{selectedTrainers.length > 1 ? 's' : ''} sélectionné
                      {selectedTrainers.length > 1 ? 's' : ''}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedTrainers([]);
                        setIsSelectAll(false);
                      }}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                    >
                      Tout désélectionner
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowBulkDeleteModal(true)}
                    className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
                  >
                    <TrashIcon className="h-4 w-4" />
                    Supprimer ({selectedTrainers.length})
                  </button>
                </div>
              )}

              {paginatedTrainers.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-5 px-6 py-16 text-center">
                  <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60">
                    <UserGroupIcon className="h-10 w-10 text-forma-500/70 dark:text-forma-400/80" />
                  </div>
                  <div className="max-w-md space-y-2">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Aucun formateur à afficher</h3>
                    <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                      Essayez une autre recherche, élargissez le filtre de spécialité avec « Réinitialiser », ou ajoutez un
                      nouveau formateur.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setSearchTerm('');
                        setSpecialityFilter('all');
                        setCurrentPage(1);
                      }}
                      className="mx-auto rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      Effacer filtres et recherche
                    </button>
                  </div>
                  <Link to="/trainers/add" className={formaLinkBtn}>
                    <PlusIcon className="h-5 w-5" />
                    <span>Ajouter un formateur</span>
                  </Link>
                </div>
              ) : (
                <>
                  <div className="border-b border-slate-200 bg-gradient-to-r from-white to-slate-50/95 px-6 py-4 dark:border-slate-700 dark:from-slate-900 dark:to-slate-950/90">
                    <h2 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">
                      Liste des formateurs
                    </h2>
                    <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-600 dark:text-slate-400">
                      <span>
                        <span className="font-semibold tabular-nums text-slate-900 dark:text-slate-100">{totalTrainers}</span>{' '}
                        résultat{totalTrainers > 1 ? 's' : ''}
                      </span>
                      <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                        ·
                      </span>
                      <span>
                        plage{' '}
                        <span className="font-medium tabular-nums text-slate-800 dark:text-slate-200">{pageSliceLabel}</span>{' '}
                        sur <span className="font-medium tabular-nums text-slate-800 dark:text-slate-200">{totalTrainers}</span>
                      </span>
                      <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                        ·
                      </span>
                      <span className="text-slate-500">{trainersPerPage} par page</span>
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] border-collapse text-left">
                      <thead className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 text-[11px] font-bold uppercase tracking-wider text-slate-500 shadow-[0_1px_0_rgba(15,23,42,0.06)] backdrop-blur-md dark:border-slate-700 dark:bg-slate-900/95 dark:text-slate-400">
                        <tr>
                          <th scope="col" className="w-12 px-4 py-3.5">
                            <span className="sr-only">Sélection</span>
                            <input
                              type="checkbox"
                              checked={isSelectAll}
                              onChange={handleSelectAll}
                              aria-label="Sélectionner tout sur cette page"
                              className="h-4 w-4 rounded border-slate-300 text-forma-600 focus:ring-forma-500 dark:border-slate-600 dark:bg-slate-900"
                            />
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            Formateur
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            Contact
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            Spécialité
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            Cours assignés
                          </th>
                          <th scope="col" className="px-4 py-3.5 text-right font-bold">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedTrainers.map((trainer, rowIdx) => (
                          <motion.tr
                            key={trainer.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: Math.min(rowIdx * 0.03, 0.35) }}
                            className={clsx(
                              'border-b border-slate-100 transition-colors dark:border-slate-800/80',
                              rowIdx % 2 === 1 && 'bg-slate-50/45 dark:bg-slate-900/40',
                              'hover:bg-forma-50/50 dark:hover:bg-forma-950/20',
                              selectedTrainers.includes(trainer.id) &&
                                'bg-forma-100/55 ring-1 ring-inset ring-forma-200/80 dark:bg-forma-950/40 dark:ring-forma-800/60'
                            )}
                          >
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <input
                                type="checkbox"
                                checked={selectedTrainers.includes(trainer.id)}
                                onChange={() => handleSelectTrainer(trainer.id)}
                                aria-label={`Sélectionner ${trainer.name}`}
                                className="h-4 w-4 rounded border-slate-300 text-forma-600 focus:ring-forma-500 dark:border-slate-600 dark:bg-slate-900"
                              />
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <div className="flex items-center">
                                <div className="mr-3 flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-forma-100 dark:bg-forma-950/50">
                                  {trainer.photo ? (
                                    <img src={trainer.photo} alt={trainer.name} className="h-10 w-10 object-cover" />
                                  ) : (
                                    <UserGroupIcon className="h-5 w-5 text-forma-600 dark:text-forma-400" />
                                  )}
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-slate-900 dark:text-slate-50">{trainer.name}</div>
                                  <div className="text-xs text-slate-600 dark:text-slate-400">
                                    {trainer.experience} ans d&apos;expérience
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <div className="text-sm text-slate-700 dark:text-slate-300">{trainer.email}</div>
                              <div className="text-xs text-slate-600 dark:text-slate-400">{trainer.phone}</div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <span className="rounded-full bg-forma-100 px-2.5 py-1 text-xs font-medium text-forma-800 dark:bg-forma-950/45 dark:text-forma-400">
                                {trainer.specialty || 'Non spécifié'}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              {(trainer.courses?.length || 0) > 0 ? (
                                <div className="flex max-w-[14rem] flex-wrap gap-1">
                                  {trainer.courses.map((course) => (
                                    <span
                                      key={course.id}
                                      className={clsx(
                                        'rounded-full px-2 py-0.5 text-[11px] font-medium',
                                        course.status === 'active' &&
                                          'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-400',
                                        course.status === 'pending' &&
                                          'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-400',
                                        course.status !== 'active' &&
                                          course.status !== 'pending' &&
                                          'bg-slate-100 text-slate-600 dark:bg-slate-600/25 dark:text-slate-400'
                                      )}
                                    >
                                      {course.title.length > 20 ? course.title.substring(0, 20) + '…' : course.title}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-sm text-slate-500 dark:text-slate-400">Aucun cours assigné</span>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5 text-right text-sm">
                              <div className="flex justify-end gap-1.5">
                                <Link
                                  to={`/trainers/${trainer.id}`}
                                  className="rounded-lg border border-slate-200 bg-white p-2 text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-forma-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-forma-400"
                                  title="Voir les détails"
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </Link>
                                <Link
                                  to={`/trainers/edit/${trainer.id}`}
                                  className="rounded-lg border border-slate-200 bg-white p-2 text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-amber-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800"
                                  title="Modifier"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </Link>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(trainer.id)}
                                  className="rounded-lg border border-slate-200 bg-white p-2 text-red-600 shadow-sm transition hover:bg-red-50 dark:border-slate-600 dark:bg-slate-900 dark:text-red-400 dark:hover:bg-red-950/30"
                                  title="Supprimer"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {totalTrainers > trainersPerPage && (
                    <div className="flex flex-col gap-4 border-t border-slate-200 px-6 py-4 dark:border-slate-600 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-center text-sm text-slate-600 dark:text-slate-400 sm:text-left">
                        Affichage <span className="font-semibold tabular-nums text-slate-800 dark:text-slate-200">{pageSliceLabel}</span> sur{' '}
                        <span className="font-semibold tabular-nums">{totalTrainers}</span>
                        {' · '}page <span className="tabular-nums">{currentPage}</span> sur <span className="tabular-nums">{totalPages}</span>
                      </p>
                      <nav className="flex flex-wrap items-center justify-center gap-1.5 sm:justify-end" aria-label="Pagination">
                        <button
                          type="button"
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          <ChevronLeftIcon className="h-4 w-4" aria-hidden />
                          Précédent
                        </button>
                        <div className="mx-1 flex flex-wrap items-center justify-center gap-1">
                          {pageNav.map((item, idx) =>
                            item.type === 'ellipsis' ? (
                              <span
                                key={'el-' + idx}
                                className="px-2 font-medium text-slate-400 dark:text-slate-500"
                                aria-hidden
                              >
                                …
                              </span>
                            ) : (
                              <button
                                key={item.page}
                                type="button"
                                onClick={() => setCurrentPage(item.page)}
                                className={clsx(
                                  'min-w-[2.5rem] rounded-lg px-2 py-1.5 text-sm font-semibold tabular-nums transition',
                                  item.page === currentPage
                                    ? 'bg-forma-600 text-white shadow-sm'
                                    : 'border border-transparent text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800'
                                )}
                              >
                                {item.page}
                              </button>
                            )
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          Suivant
                          <ChevronRightIcon className="h-4 w-4" aria-hidden />
                        </button>
                      </nav>
                    </div>
                  )}
                </>
              )}
            </UiCard>
          </div>
        }
      />
      <Route
        path="/add"
        element={
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mb-6">
              <Link to="/trainers" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 flex items-center">
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Retour à la liste
              </Link>
            </div>
            <TrainerForm />
          </motion.div>
        }
      />
      <Route
        path="/edit/:id"
        element={<EditTrainerRoute trainers={trainers} />}
      />
      <Route
        path="/:id"
        element={<ViewTrainerRoute trainers={trainers} />}
      />
      </Routes>

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-600 dark:bg-slate-900 w-full max-w-md"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <TrashIcon className="h-6 w-6 text-red-400" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50">
                Confirmer la suppression
              </h3>
            </div>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              Êtes-vous sûr de vouloir supprimer {selectedTrainers.length} formateur{selectedTrainers.length > 1 ? 's' : ''} ? 
              Cette action est irréversible.
            </p>
            <div className="flex gap-3 w-full">
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                className="flex-1 px-4 py-2 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleBulkDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Supprimer
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

// Route components pour edit et view
const EditTrainerRoute = () => {
  const { id } = useParams();
  const { getById, isLoading } = useLocalStorage('trainers');
  const [trainer, setTrainer] = useState(null);
  
  useEffect(() => {
    const trainerData = getById(id);
    setTrainer(trainerData);
  }, [id, getById]);

  if (isLoading || !trainer) {
    return <div className="text-slate-900 dark:text-slate-50">Chargement...</div>;
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
    >
      <TrainerForm isEditing={true} initialData={trainer} />
    </motion.div>
  );
};

const ViewTrainerRoute = () => {
  const { id } = useParams();
  const { getById, isLoading } = useLocalStorage('trainers');
  const [trainer, setTrainer] = useState(null);
  
  useEffect(() => {
    const trainerData = getById(id);
    setTrainer(trainerData);
  }, [id, getById]);

  if (isLoading || !trainer) {
    return <div className="text-slate-900 dark:text-slate-50">Chargement...</div>;
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
    >
      <TrainerDetail trainer={trainer} />
    </motion.div>
  );
};

export default TrainersPage; 